/**
 * Invoice Template API Hooks
 * React Query hooks for invoice template operations
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import config from "@/lib/config";
import type { InvoiceTemplate, UpdateInvoiceTemplate } from "../types/template.types";

const TEMPLATE_KEYS = {
  all: ["invoice-template"] as const,
  detail: () => [...TEMPLATE_KEYS.all, "detail"] as const,
};

/**
 * Helper function for API calls with credentials
 */
async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem("auth-data");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${config.apiUrl}${url}`, {
    ...options,
    credentials: "include",
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch the user's invoice template settings
 * Returns template or default values if none exists
 */
export function useInvoiceTemplate() {
  return useQuery<InvoiceTemplate>({
    queryKey: TEMPLATE_KEYS.detail(),
    queryFn: () => fetchWithAuth("/api/invoice-template"),
  });
}

/**
 * Fetch a specific template by ID
 */
export function useTemplateById(
  templateId: string,
  options?: { enabled?: boolean }
) {
  return useQuery<InvoiceTemplate>({
    queryKey: [...TEMPLATE_KEYS.all, templateId],
    queryFn: () => fetchWithAuth(`/api/invoice-template/${templateId}`),
    enabled: options?.enabled ?? !!templateId,
  });
}

/**
 * Update the user's invoice template settings
 */
export function useUpdateInvoiceTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateInvoiceTemplate) => {
      return fetchWithAuth("/api/invoice-template", {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.detail() });
    },
  });
}

/**
 * Upload a logo image for the invoice template
 */
export function useUploadLogo() {
  return useMutation({
    mutationFn: async (file: File) => {
      const token = localStorage.getItem("auth-data");
      const formData = new FormData();
      formData.append("logo", file);

      const response = await fetch(`${config.apiUrl}/api/invoice-template/upload-logo`, {
        method: "POST",
        credentials: "include",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: "Upload failed" }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      return response.json() as Promise<{ 
        logoUrl: string;
        originalWidth: number;
        originalHeight: number;
        originalAspectRatio: number;
      }>;
    },
  });
}
